import { SearchPersonResult } from "components/domain/SearchBar/types";
import { NsKey } from "enums/NsKey";
import { PersonSearchBar } from "features/subscription-process/components/person-search/PersonSearchBar";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { AlertCircle, Check, Search } from "lucide-react";
import { FC } from "react";
import { LP_SURFACE, LP_TEXT } from "./legalProtectionTheme";

interface ExistingCompanySearchProps {
  onSelect: (person: SearchPersonResult) => void;
  /** Refusal of the picked company, shown under the search. */
  error?: string | null;
  /** A company was picked: the insert says so with a badge. */
  isSelected?: boolean;
}

/**
 * Search of an existing legal entity, in the light insert at the top of a side panel. The page
 * itself carries no search bar. The bar is `PersonSearchBar` as it is: only the insert around it
 * belongs to this parcours.
 */
export const ExistingCompanySearch: FC<ExistingCompanySearchProps> = ({ onSelect, error, isSelected }) => {
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);

  return (
    <div className={`flex flex-col gap-3.5 rounded-xl border border-primary/20 p-5 ${LP_SURFACE.totals}`}>
      <div className="flex flex-col gap-1">
        <p className={`flex items-center gap-2 text-[16px] font-bold ${LP_TEXT.title}`}>
          <Search className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SEARCH_EXISTING_COMPANY")}
        </p>
        <p className={`text-[13px] ${LP_TEXT.secondary}`}>
          {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.PANEL.SEARCH_HINT")}
        </p>
      </div>

      <PersonSearchBar
        onSelect={(person) => person && onSelect(person)}
        withNaturalPerson={false}
        withLegalEntity
        size="lg"
        className="w-full"
      />

      {isSelected && (
        <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-primary/10 px-3 py-1 text-[12px] font-bold text-primary">
          <Check className="h-3 w-3 flex-none" aria-hidden="true" />
          {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARY.EXISTING")}
        </span>
      )}

      {error && (
        <p className="inline-flex items-center gap-1.5 text-[13px] text-red-600">
          <AlertCircle className="h-4 w-4 flex-none" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
};
